<template>
  <f7-page :page-content="false">
    <f7-navbar :title="$t('title.requestDetails')" :back-link="$t('action.back')"/>
    <f7-page-content>
      <collapsing-header id="header">
        <div slot="large" id="expanded-header">
          <div style="height: 1px"/>
          <request-card :request="request"/>
          <f7-list v-if="isProcessed">
            <f7-list-item v-if="isApproved" :title="$t('label.approved')" text-color="blue"
                          :footer="processedFooter">
              <f7-icon slot="media" f7="checkmark_circle_fill" color="blue"/>
            </f7-list-item>
            <f7-list-item v-else :title="$t('label.declined')" text-color="pink"
                          :footer="processedFooter">
              <f7-icon slot="media" f7="xmark_circle_fill" color="pink"/>
            </f7-list-item>
            <f7-list-item v-if="request.denialReason" :title="$t('label.reason')"
                          :footer="request.denialReason">
              <f7-icon slot="media" f7="chat_bubble_text"/>
            </f7-list-item>
            <f7-list-item v-if="request.acceptanceNote" :title="$t('label.notes')"
                          :footer="request.acceptanceNote">
              <f7-icon slot="media" f7="chat_bubble_text"/>
            </f7-list-item>
            <f7-list-item v-if="request.totalSpent" :title="$t('label.actualSpend')" :after="spend">
              <f7-icon slot="media" f7="money_pound"/>
            </f7-list-item>
            <f7-list-item v-if="request.wasRefunded" :title="$t('label.refunded')"
                          :after="$t('label.returned', {amount: refund})">
              <f7-icon slot="media" f7="arrow_uturn_left"/>
            </f7-list-item>
          </f7-list>
          <f7-list v-if="isManaging && !isProcessed" v-beta.oldContent>
            <f7-list-item>
              <f7-input type="textarea" :placeholder="$t('placeholder.enterMessage')" :value="message"
                        @input="message = $event.target.value"/>
            </f7-list-item>
          </f7-list>
          <f7-block>
            <f7-row v-if="isManaging && !isProcessed">
              <f7-col>
                <f7-button large fill color="pink" @click="declineRequest">{{$t('action.decline')}}
                </f7-button>
              </f7-col>
              <f7-col>
                <f7-button large fill @click="approveRequest">{{$t('action.approve')}}</f7-button>
              </f7-col>
            </f7-row>
            <div v-else-if="isMine">
              <f7-button v-if="!isProcessed" large fill color="pink" @click="cancelRequest">
                {{$t('action.cancelRequest')}}
              </f7-button>
              <f7-button v-else-if="canRefund" large fill @click="refundRequest">
                {{$t('action.returnUnspent',
                {amount:
                refund})}}
              </f7-button>
            </div>
          </f7-block>
          <hairline/>
        </div>
        <div slot="small" id="collapsed-header" ref="smallHeader">
          <div class="c-h-left">
            <f7-link v-if="!isProcessed && isManaging && !isMine" icon-f7="xmark_circle" color="pink" small
                     @click="declineRequest"/>
            <f7-link v-if="isMine && !isProcessed" icon-f7="xmark_circle" color="pink" small @click="cancelRequest"/>
            <f7-link v-if="isApproved" icon-f7="checkmark_circle_fill" color="blue" small/>
            <f7-link v-if="isProcessed && !isApproved" icon-f7="xmark_circle_fill" color="pink" small/>
          </div>
          <f7-list id="c-h-list" no-hairlines>
            <f7-list-item :after="requester">
              <f7-icon slot="media" f7="creditcard"/>
            </f7-list-item>
            <f7-list-item :after="formatMoney(request.currency, request.amount)">
              <f7-icon slot="media" f7="money_pound_round"/>
            </f7-list-item>
            <f7-list-item :after="request.reason">
              <f7-icon slot="media" f7="briefcase"/>
            </f7-list-item>
          </f7-list>
          <div class="c-h-right">
            <f7-link v-if="!isProcessed && isManaging" icon-f7="checkmark_circle" small @click="approveRequest"/>
            <!-- hidden icon just to fill the space (otherwise it gets lopsided) -->
            <f7-link v-else icon-f7="checkmark_circle" small style="visibility: hidden"/>
          </div>
        </div>
      </collapsing-header>
      <activity-feed :request-id="requestId" v-beta/>
    </f7-page-content>
  </f7-page>
</template>

<script>
  import api from 'aio-api'
  import helpers from '@/utils/helpers'
  import CollapsingHeader from "@/components/CollapsingHeader";
  import ActivityFeed from "@/components/ActivityFeed";
  import Hairline from "@/components/Hairline";
  import RequestCard from "@/components/RequestCard";

  export default {
    name: 'request-page',
    computed: {
      budget () {
        const budget = this.budgets.find(b => b.potId === this.request.potId)
        if (budget) {
          const balance = this.formatMoney(budget.currency, budget.balance)
          return this.isManaging && !this.isProcessed ? this.$t('title.balanceAvailable', {
            budget: budget.name,
            amount: balance
          }) : budget.name
        } else return ''
      },
      canRefund () {
        return this.isApproved && !this.request.wasRefunded && (!this.request.totalSpent || this.request.totalSpent < this.request.amount)
      },
      isApproved () {
        return this.request.status === 'approved'
      },
      isManaging () {
        return !!this.$f7route.params.manage
      },
      isMine () {
        return this.request.userId === this.currentUserId
      },
      isProcessed () {
        return !!this.request.status
      },
      processor () {
        const processor = this.myTeam.find(u => u.id === this.request.processorId)
        return processor ? this.userName(processor) : ''
      },
      processedFooter () {
        return this.$t('label.by', {user: this.processor, date: this.toDateTime(this.request.processDate)})
      },
      refund () {
        return this.formatMoney(this.request.currency, this.refundable)
      },
      refundable () {
        return this.request.amount - (this.request.totalSpent ? this.request.totalSpent : 0)
      },
      requester () {
        const user = this.myTeam.find(u => u.id === this.request.userId)
        return user ? this.userName(user) : ''
      },
      spend () {
        return this.formatMoney(this.request.currency, this.request.totalSpent)
      },
      requestId () {
        return parseInt(this.$f7route.params.id)
      }
    },
    async created () {
      this.request = await api.request.get(this.requestId)
      if (this.isManaging && !this.isProcessed) {
        const card = await api.getCard(this.request.userId)
        if (card.status === 'Suspended')
          this.$f7.dialog.alert(`The user's card is currently frozen. Please be aware that they won't be able to make any purchases with it.`, '')
      }
      this.$store.dispatch('fetchTeam')
      this.$store.dispatch('budgets/fetch')
    },
    data () {
      return {
        message: '',
        request: {},
      }
    },
    methods: {
      async approveRequest () {
        try {
          await api.approveRequest(this.request.userId, this.request.id, this.message ? this.message : undefined)
          this.request = await api.request.get(this.request.id)
          this.$store.dispatch('requests/fetchManaged')
        } catch (error) {
          //TODO: i18n error message
          this.$f7.dialog.alert(error.response.data, '')
        }
      },
      async cancelRequest () {
        try {
          this.$f7.dialog.confirm(this.$t('dialog.cancelRequest'), '', async () => {
            await api.cancelRequest(this.request.id)
            this.$store.dispatch('requests/fetchOwn')
            this.goBack()
          })
        } catch (error) {
          //TODO: i18n error message
          this.$f7.dialog.alert(error.response.data, '')
        }
      },
      async declineRequest () {
        try {
          await api.declineRequest(this.request.userId, this.request.id, this.message ? this.message : undefined)
          this.request = await api.getRequest(this.request.id)
          this.$store.dispatch('requests/fetchManaged')
        } catch (error) {
          //TODO: i18n error message
          this.$f7.dialog.alert(error.response.data, '')
        }
      },
      async refundRequest () {
        try {
          await api.postMyCardRefund(this.request.userId, this.refundable, this.request.potId, this.request.id)
          this.request = await api.getRequest(this.request.id)
          this.$store.dispatch('requests/fetchOwn')
        } catch (error) {
          //TODO: i18n error message
          this.$f7.dialog.alert(error.response.data, '')
        }
      },
    },
    mixins: [helpers],
    components: {Hairline, ActivityFeed, CollapsingHeader, RequestCard},
  }
</script>

<style lang="scss" scoped>
  #header {
    background-color: white;
  }

  #collapsed-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-bottom: solid 0.5px #DDD;
  }

  #expanded-header > .list {
  }

  .c-h-left, .c-h-right {
    min-width: 4em;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>

<style lang="scss">
  // Can't be scoped or it doesn't apply properly
  #c-h-list {
    margin: 0;
    min-width: 0;
    width: 100%;

    .item-inner {
      width: 100%;
      display: block;

      span {
        margin-left: auto;
        overflow: hidden;
        display: block;
        text-overflow: ellipsis;
      }
    }
  }
</style>
